/**
 * La page d'un stage, sans savoir d'où viennent ses données.
 *
 * ## Pourquoi cette séparation existe
 *
 * Le même composant est rendu deux fois : par le navigateur, après un appel à
 * Supabase ; et au BUILD, par `renderToString`, pour produire le HTML que les
 * robots reçoivent. Un composant qui va chercher ses données lui-même ne peut
 * pas être rendu au build — il rendrait une page vide, puisque `useEffect` ne
 * s'exécute pas hors navigateur.
 *
 * D'où la règle : cette vue ne fait que présenter. Elle ne lit rien, n'attend
 * rien, n'a pas d'état. C'est ce qui garantit que la page pré-rendue et la page
 * vécue disent exactement la même chose — elles sortent du même code.
 */

import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users } from 'lucide-react';
import {
  ORGANISATEUR,
  adresseComplete,
  commune,
  NOM_DU_TYPE,
  periodeEnToutesLettres,
  inscriptionsOuvertes,
  phraseDeDisponibilite,
  phrasesDuStage,
  prixEnEuros,
  trancheDAge,
  typeDe,
  type StagePublic,
} from '../../lib/seo/stage';

interface Props {
  stage: StagePublic;
  voisins: StagePublic[];
}

const StageVue = ({ stage, voisins }: Props) => {
  const phrases = phrasesDuStage(stage);
  const adresse = adresseComplete(stage);
  const ouvert = inscriptionsOuvertes(stage);
  /* Un stage pas encore ouvert n'est pas complet : ses places sont toutes libres. */
  const complet = ouvert && stage.places_restantes <= 0;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Fil d'Ariane — le même que le BreadcrumbList posé en JSON-LD. */}
      <nav aria-label="Fil d’Ariane" className="text-sm text-gray-500 mb-5">
        <ol className="flex flex-wrap items-center gap-1">
          <li>
            <Link to="/" className="hover:text-primary-700">
              Accueil
            </Link>
          </li>
          <li aria-hidden="true">›</li>
          <li>
            <Link to="/stages" className="hover:text-primary-700">
              Stages
            </Link>
          </li>
          <li aria-hidden="true">›</li>
          <li className="text-gray-700">{commune(stage)}</li>
        </ol>
      </nav>

      <header className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {stage.stage} — {trancheDAge(stage.age_min, stage.age_max)} à {commune(stage)}
        </h1>
        <p className="text-gray-600">
          {NOM_DU_TYPE[typeDe(stage)].replace(/^./, (c) => c.toUpperCase())} organisé par{' '}
          {ORGANISATEUR}
          {stage.periode && stage.annee ? ` — ${stage.periode} ${stage.annee}` : ''}
          {stage.semaine ? `, semaine ${stage.semaine}` : ''}.
        </p>
      </header>

      {stage.image_url && (
        <img
          src={stage.image_url}
          alt={`Stage ${stage.stage} à ${commune(stage)}`}
          className="w-full rounded-xl mb-6 object-cover max-h-72"
          loading="lazy"
        />
      )}

      {/*
        Le résumé en toutes lettres. C'est le bloc qu'un moteur ou une IA lit
        pour répondre — d'où des phrases complètes plutôt qu'un tableau de
        pictogrammes.
      */}
      <section className="prose prose-sm max-w-none mb-6">
        {stage.description?.trim() && <p className="text-gray-800">{stage.description}</p>}
        {phrases.map((p) => (
          <p key={p} className="text-gray-800">
            {p}
          </p>
        ))}
      </section>

      {/* Les mêmes faits, en repères visuels — un complément, jamais le seul support. */}
      <dl className="grid sm:grid-cols-2 gap-4 mb-8">
        <div className="border border-gray-200 rounded-lg p-4">
          <dt className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-1">
            <Calendar className="h-4 w-4" aria-hidden="true" />
            Dates
          </dt>
          <dd className="text-gray-900">
            {periodeEnToutesLettres(stage.start_date, stage.end_date)}
            {stage.nombre_jours ? ` — ${stage.nombre_jours} jours` : ''}
          </dd>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <dt className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-1">
            <MapPin className="h-4 w-4" aria-hidden="true" />
            Lieu
          </dt>
          <dd className="text-gray-900">
            {stage.centre}
            {adresse && <span className="block text-sm text-gray-600">{adresse}</span>}
          </dd>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <dt className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-1">
            <Users className="h-4 w-4" aria-hidden="true" />
            Âge
          </dt>
          <dd className="text-gray-900">De {trancheDAge(stage.age_min, stage.age_max)}</dd>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <dt className="text-sm font-medium text-gray-500 mb-1">Tarifs</dt>
          <dd className="text-gray-900">
            <span className="font-medium">{prixEnEuros(stage.prix_normal)}</span>
            {stage.prix_reduit !== null && (
              <span className="block text-sm text-gray-600">
                Tarif réduit : {prixEnEuros(stage.prix_reduit)}
              </span>
            )}
            {stage.prix_local !== null && (
              <span className="block text-sm text-gray-600">
                Tarif local : {prixEnEuros(stage.prix_local)}
              </span>
            )}
          </dd>
        </div>
      </dl>

      <div className="border-t border-gray-200 pt-6 mb-10">
        {/*
          La MEME phrase que le bloc explicite, pour que la page ne se
          contredise pas. Avant l'ouverture, elle annonce le moment plutot que
          de compter des places qu'on ne peut pas encore prendre.
        */}
        <p
          className={`mb-4 font-medium ${
            !ouvert ? 'text-amber-700' : complet ? 'text-red-700' : 'text-green-700'
          }`}
        >
          {phraseDeDisponibilite(stage)}
        </p>

        {/*
          Pas de bouton d'inscription tant que ce n'est pas ouvert : le
          declencheur en base refuserait la commande, et une famille envoyee
          dans un tunnel qui se referme sur un message d'erreur revient moins
          volontiers que celle a qui on a dit la date.
        */}
        {ouvert ? (
          <Link
            to="/activities"
            className={complet ? 'btn-outline' : 'btn-primary'}
            aria-label={`S’inscrire : ${stage.stage} à ${commune(stage)}`}
          >
            {complet ? 'Voir les autres stages' : 'Inscrire mon enfant'}
          </Link>
        ) : (
          <p className="text-sm text-gray-600">
            Vous pourrez inscrire votre enfant à partir de cette date, depuis cette
            page ou depuis votre compte.
          </p>
        )}
      </div>

      {/* Le maillage interne : d'ici vers les stages voisins (§18). */}
      {voisins.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Autres activités à {commune(stage)} et pendant la même période
          </h2>
          <ul className="space-y-2">
            {voisins.map((v) => (
              <li key={v.slug}>
                <Link
                  to={`/stages/${v.slug}`}
                  className="text-primary-700 hover:text-primary-900 hover:underline"
                >
                  {v.stage} — {trancheDAge(v.age_min, v.age_max)} à {commune(v)},{' '}
                  {periodeEnToutesLettres(v.start_date, v.end_date)}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default StageVue;
